import { useState } from 'react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { RenameFolderDialog } from '@/components/RenameFolderDialog'
import { ChangeFolderCategoryDialog } from '@/components/ChangeFolderCategoryDialog'
import { DeleteFolderDialog } from '@/components/DeleteFolderDialog'
import { SquarePen, Tag, Trash } from 'lucide-react'
import type { LibraryFolder } from '@/lib/schemas'

interface FolderContextMenuProps {
  folder: LibraryFolder
  /** Whether the current user may delete this folder (creator or admin). */
  canDelete: boolean
  /** When true (viewer), no menu is rendered — children pass through bare. */
  disabled?: boolean
  children: React.ReactNode
}

export function FolderContextMenu({
  folder,
  canDelete,
  disabled = false,
  children,
}: FolderContextMenuProps) {
  const [renameOpen, setRenameOpen] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Viewers get no curation menu at all — the row is read-only.
  if (disabled) {
    return <>{children}</>
  }

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => setRenameOpen(true)}>
            <SquarePen className="h-4 w-4 mr-2" />
            Rename
          </ContextMenuItem>
          <ContextMenuItem onClick={() => setCategoryOpen(true)}>
            <Tag className="h-4 w-4 mr-2" />
            Change Category
          </ContextMenuItem>
          {canDelete && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => setDeleteOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      <RenameFolderDialog
        folder={folder}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />
      <ChangeFolderCategoryDialog
        folder={folder}
        open={categoryOpen}
        onOpenChange={setCategoryOpen}
      />
      {canDelete && (
        <DeleteFolderDialog
          folder={folder}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      )}
    </>
  )
}
